
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { usePersona } from '@/hooks/use-persona';
import { CsuiteRole } from '@/types/personas';
import { ChevronDown, Sparkles } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

export function PersonaToggle() {
  const { currentPersona, setPersona, personaConfig, getAllPersonas } = usePersona();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const personas = getAllPersonas();

  return (
    <div className="relative">
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className={`relative overflow-hidden border-2 border-transparent ${personaConfig.gradient} text-white hover:opacity-90 transition-all duration-300 shadow-lg ${
          isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-3'
        }`}
      >
        <div className="flex items-center gap-2">
          <span className={isMobile ? 'text-base' : 'text-lg'}>{personaConfig.icon}</span>
          <div className="text-left">
            <div className={`font-semibold ${isMobile ? 'text-xs' : 'text-sm'}`}>{personaConfig.id}</div>
            <div className={`opacity-90 ${isMobile ? 'text-xs hidden sm:block' : 'text-xs'}`}>{personaConfig.title}</div>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
        <Sparkles className="absolute top-1 right-1 h-3 w-3 opacity-50" />
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className={`absolute top-full mt-2 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden ${
            isMobile 
              ? 'fixed left-4 right-4 top-auto bottom-auto max-w-none w-auto' 
              : 'w-80 right-0'
          }`}>
            <div className="p-3 bg-gray-50 border-b">
              <h3 className={`font-semibold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>Switch C-Suite Persona</h3>
              <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Each role provides specialized AI assistance</p>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {personas.map((persona) => (
                <button
                  key={persona.id}
                  onClick={() => {
                    setPersona(persona.id);
                    setIsOpen(false);
                  }}
                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors border-l-4 ${
                    currentPersona === persona.id 
                      ? 'border-l-blue-500 bg-blue-50' 
                      : 'border-l-transparent'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`rounded-lg ${persona.gradient} text-white shadow-sm flex items-center justify-center ${
                      isMobile ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-lg'
                    }`}>
                      <span>{persona.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`font-semibold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>{persona.id}</h4>
                        {currentPersona === persona.id && (
                          <span className={`px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium ${isMobile ? 'text-xs' : 'text-xs'}`}>Active</span>
                        )}
                      </div>
                      <p className={`text-gray-600 mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>{persona.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {persona.focus.slice(0, isMobile ? 2 : 3).map((focus) => (
                          <span key={focus} className={`px-2 py-1 bg-gray-100 text-gray-600 rounded ${isMobile ? 'text-xs' : 'text-xs'}`}>
                            {focus}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
